import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignatureModal from './SignatureModal';
import {
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  isGenderField,
  isMaritalStatusField,
} from '../constants/formOptions';
import './DataPanel.css';

function DataPanel({
  collectedData,
  missingFields,
  completionPercentage,
  onFieldEdit,
  onExport,
  onViewForm,
  formType,
}) {
  const { t } = useTranslation();
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [isProgressOpen, setIsProgressOpen] = useState(true);
  const [isCollectedOpen, setIsCollectedOpen] = useState(true);
  const [isMissingOpen, setIsMissingOpen] = useState(true);

  const collectedCount = Object.keys(collectedData || {}).length;
  const missingCount = missingFields?.length || 0;
  const totalFields = collectedCount + missingCount;
  const progress =
    completionPercentage != null && completionPercentage > 0
      ? completionPercentage
      : totalFields > 0
        ? (collectedCount / totalFields) * 100
        : 0;

  const handleStartEdit = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  const handleSaveEdit = () => {
    const val = editValue != null ? String(editValue).trim() : '';
    if (editingField && val) {
      onFieldEdit(editingField, val);
    }
    setEditingField(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const getFieldLabel = (field) => {
    const key = `fields.${field}`;
    const translation = t(key);
    return translation !== key ? translation : field.replace(/_/g, ' ');
  };

  const isSignatureValue = (value) =>
    typeof value === 'string' && value.startsWith('data:image/');

  return (
    <div className="data-panel">
      <div className={`data-panel-section progress-section${!isProgressOpen ? ' collapsed' : ''}`}>
        <div className="section-header-row">
          <h3 className="section-title">{t('dataPanel.completionProgress')}</h3>
          <button
            type="button"
            className="collapse-toggle"
            onClick={() => setIsProgressOpen((prev) => !prev)}
          >
            {isProgressOpen ? t('dataPanel.hideCollected') : t('dataPanel.showCollected')}
          </button>
        </div>
        {isProgressOpen && (
          <>
            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="progress-text">{Math.round(progress)}%</span>
            </div>
            <div className="progress-stats">
              <span className="stat completed">
                <span className="stat-icon">✓</span>
                {collectedCount} {t('dataPanel.collectedData').toLowerCase()}
              </span>
              <span className="stat missing">
                <span className="stat-icon">○</span>
                {missingCount} {t('dataPanel.missingFields').toLowerCase()}
              </span>
            </div>
          </>
        )}
      </div>

      <div className="data-panel-section collected-section">
        <div className="section-header-row">
          <h3 className="section-title">
            {t('dataPanel.collectedData')}
            <span className="section-count">{collectedCount}</span>
          </h3>
          {collectedCount > 0 && (
            <button
              type="button"
              className="collapse-toggle"
              onClick={() => setIsCollectedOpen((prev) => !prev)}
            >
              {isCollectedOpen ? t('dataPanel.hideCollected') : t('dataPanel.showCollected')}
            </button>
          )}
        </div>

        {isCollectedOpen &&
          (collectedCount > 0 ? (
            <div className="fields-list">
              {Object.entries(collectedData || {}).map(([field, value]) => (
                <div key={field} className="field-item">
                  {editingField === field && field !== 'firma_solicitante' ? (
                    <div className="field-edit">
                      {isGenderField(field) ? (
                        <select
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          className="data-panel-select"
                          autoFocus
                        >
                          <option value="">—</option>
                          {GENDER_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : isMaritalStatusField(field) ? (
                        <select
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          className="data-panel-select"
                          autoFocus
                        >
                          <option value="">—</option>
                          {MARITAL_STATUS_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit();
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                        />
                      )}
                      <div className="edit-actions">
                        <button type="button" className="save-btn" onClick={handleSaveEdit}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button type="button" className="cancel-btn" onClick={handleCancelEdit}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="field-info">
                        <span className="field-label">{getFieldLabel(field)}</span>
                        {field === 'firma_solicitante' && isSignatureValue(value) ? (
                          <div className="signature-value">
                            <img src={value} alt={t('dataPanel.signature')} />
                            <span className="signature-caption">{t('dataPanel.signatureCaptured')}</span>
                          </div>
                        ) : (
                          <span className="field-value">{String(value)}</span>
                        )}
                      </div>
                      {field === 'firma_solicitante' ? (
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => setShowSignatureModal(true)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M12 20h9M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => handleStartEdit(field, value)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>{t('chat.placeholder')}...</p>
            </div>
          ))}
      </div>

      {missingCount > 0 && (
        <div className="data-panel-section missing-section">
          <div className="section-header-row">
            <h3 className="section-title">
              {t('dataPanel.missingFields')}
              <span className="section-count warning">{missingCount}</span>
            </h3>
            <button
              type="button"
              className="collapse-toggle"
              onClick={() => setIsMissingOpen((prev) => !prev)}
            >
              {isMissingOpen ? t('dataPanel.hideMissing') : t('dataPanel.showMissing')}
            </button>
          </div>
          {isMissingOpen && (
            <div className="missing-fields-list">
              {missingFields.slice(0, 8).map((field) => (
                <span key={field} className="missing-field-tag">
                  {getFieldLabel(field)}
                </span>
              ))}
              {missingCount > 8 && (
                <span className="more-fields">+{missingCount - 8} {t('dataPanel.more')}</span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="data-panel-section actions-section">
        {formType === 'REPRESENTATIVE' && (
          <button
            type="button"
            className="signature-button"
            onClick={() => setShowSignatureModal(true)}
          >
            {collectedData?.firma_solicitante ? t('dataPanel.editSignature') : t('dataPanel.addSignature')}
          </button>
        )}
        {completionPercentage >= 100 && missingCount === 0 && onViewForm && (
          <button type="button" className="view-form-button" onClick={onViewForm}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {t('dataPanel.viewForm')}
          </button>
        )}
        <button
          type="button"
          className="export-button"
          onClick={onExport}
          disabled={collectedCount === 0}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {t('dataPanel.downloadData')}
        </button>
      </div>
      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSave={(dataUrl) => onFieldEdit('firma_solicitante', dataUrl)}
        initialDataUrl={collectedData?.firma_solicitante}
      />
    </div>
  );
}

export default DataPanel;
